/** Shared Recharts tooltip + chart styling constants for dark/light mode consistency */
import type React from "react";

/** Theme-aware tooltip contentStyle for Recharts Tooltip component.
 *  Note: Recharts inlines its own colors on the inner <p>/<span> nodes, so
 *  contentStyle.color alone is NOT enough — pair with TOOLTIP_LABEL_STYLE and
 *  TOOLTIP_ITEM_STYLE below to guarantee legibility on dark cockpit canvases. */
export const TOOLTIP_STYLE: React.CSSProperties = {
  fontSize: 11,
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  color: "hsl(var(--popover-foreground))",
  boxShadow: "0 4px 12px hsl(0 0% 0% / 0.25)",
};

/** Tooltip label (header row) — overrides Recharts default `color: #666`. */
export const TOOLTIP_LABEL_STYLE: React.CSSProperties = {
  color: "hsl(var(--popover-foreground))",
  fontWeight: 600,
  marginBottom: 2,
};

/** Tooltip item (each series row) — overrides Recharts default `color: <fill>`
 *  which becomes unreadable when the series color matches the dark popover bg.
 *  We keep series text legible while letting the colored swatch carry hue. */
export const TOOLTIP_ITEM_STYLE: React.CSSProperties = {
  color: "hsl(var(--popover-foreground))",
};

/** Convenience spread: pass to <Tooltip {...TOOLTIP_PROPS} /> */
export const TOOLTIP_PROPS = {
  contentStyle: TOOLTIP_STYLE,
  labelStyle: TOOLTIP_LABEL_STYLE,
  itemStyle: TOOLTIP_ITEM_STYLE,
  cursor: { fill: "hsl(var(--muted) / 0.3)" },
} as const;

/** Theme-aware CartesianGrid stroke */
export const GRID_STROKE = "hsl(var(--border))";

/** Axis tick/stroke color */
export const AXIS_STROKE = "hsl(var(--muted-foreground))";

/** Standard chart color palette using CSS variables */
export const CHART_COLORS = {
  primary: "hsl(var(--chart-1))",
  success: "hsl(var(--chart-2))",
  accent: "hsl(var(--chart-3))",
  purple: "hsl(var(--chart-4))",
  warning: "hsl(var(--chart-5))",
  destructive: "hsl(var(--destructive))",
  info: "hsl(var(--info))",
  // Cockpit rule: never grey on dark canvas. "muted" slot resolves to a vivid violet
  // so neutral / "other" series stay legible and on-brand on the black background.
  muted: "hsl(265 75% 65%)",
};
